import { useState, useMemo, useCallback } from 'react';
import { Menu, MenuItem } from '@mui/material';
import {
  DocketMobileCard,
  DocketMobilePagination,
  FlexContainer,
} from '@act/storybook';
import { MenuItemConfig } from 'toolkit/tables/types/menu.types';
import { PaginationBounds } from 'toolkit/tables/types/table.types';
import { useAppNavigation } from 'react-components/app/routing/routes';
import { TodoTypeDto } from '@act/shared/data-transfer-objects';
import {
  formattedDateTimeFromTodo,
  formatEventType,
  formatTaskType,
} from 'react-components/docket/components/utilities/docketFormatters';
import { TodoDtoWithMember } from './docket-types';

// --- Component Props Definition ---

interface DocketFollowUpMobileViewProps {
  events: TodoDtoWithMember[]; // paginatedEvents from the hook
  totalItems: number; // filteredEventsCount from the hook
  bounds: PaginationBounds; // bounds from the hook
  // setBounds is passed down so the pagination component can update the hook's state
  setBounds: React.Dispatch<React.SetStateAction<PaginationBounds>>;
  menuItems: MenuItemConfig<TodoDtoWithMember>[]; // menuItems from the hook
}

const DocketFollowUpMobileView = ({
  events,
  totalItems,
  bounds,
  setBounds,
  menuItems,
}: DocketFollowUpMobileViewProps) => {
  const navigate = useAppNavigation();

  // --- 1. UI State (Mobile Menu) ---
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [selectedMobileItem, setSelectedMobileItem] =
    useState<TodoDtoWithMember | null>(null);

  // --- 2. Mobile Menu Handlers (Purely UI) ---

  const handleOpenMobileMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>, item: TodoDtoWithMember) => {
      setMobileMenuAnchor(event.currentTarget);
      setSelectedMobileItem(item);
    },
    []
  );

  const handleCloseMobileMenu = useCallback(() => {
    setMobileMenuAnchor(null);
    setSelectedMobileItem(null);
  }, []);

  const handleMobileMenuItemClick = useCallback(
    async (callback: (data: TodoDtoWithMember) => void) => {
      if (selectedMobileItem) {
        // The callback executes the business logic defined in the hook
        await callback(selectedMobileItem);
      }
      handleCloseMobileMenu();
    },
    [selectedMobileItem, handleCloseMobileMenu]
  );

  // --- 3. Memoized Data (for Menu visibility) ---

  // Filter menu items to only show visible ones based on the selected item
  const visibleMenuItems = useMemo(() => {
    if (!selectedMobileItem) return [];
    return menuItems.filter(item => {
      if (!item.hidden) return true;
      if (typeof item.hidden === 'function') {
        return !item.hidden(selectedMobileItem);
      }
      return !item.hidden;
    });
  }, [selectedMobileItem, menuItems]);

  // --- 4. Render Logic ---

  return (
    <FlexContainer
      direction="column"
      gap={2}
      width="100%"
      padding="16px 0 0 0"
      height="auto"
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {/* Render Mobile Cards */}
      {events.map(event => {
        const { date, time } = formattedDateTimeFromTodo(event);
        const isCallWithoutMember =
          (event.externalItemType === 'call' ||
            event.externalItemType === 'feedback_call') &&
          !event.member;

        const handleGotoAction = () => {
          if (event.subjectId && event.userId) {
            navigate.toMemberPage(
              event.userId,
              event.subjectId,
              event.externalItemType && event.externalItemId
                ? {
                    externalType: event.externalItemType,
                    externalId: event.externalItemId,
                  }
                : undefined
            );
          } else if (
            event.type === TodoTypeDto.CALL_AUDIT ||
            isCallWithoutMember
          ) {
            navigate.toCalls({ onlyUnassignedCalls: true });
          }
        };

        return (
          <DocketMobileCard
            key={event.id}
            member={event.member}
            topic={`${formatEventType(event)} - ${formatTaskType(event)}`}
            date={date}
            time={time}
            gotoAction={handleGotoAction}
            // Pass the item into the open menu handler
            openMenu={e => handleOpenMobileMenu(e, event)}
          />
        );
      })}

      {/* Render Mobile Pagination */}
      <DocketMobilePagination
        page={bounds.pageStart}
        pageSize={bounds.pageSize}
        totalItems={totalItems}
        onPageChange={(newPage: number) => {
          // Directly calls setBounds from the hook via props
          setBounds(prevBounds => ({
            ...prevBounds,
            pageStart: newPage,
          }));
        }}
      />
      {/* Render Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleCloseMobileMenu}
      >
        {visibleMenuItems.map((item, index) => (
          <MenuItem
            key={index}
            onClick={() => handleMobileMenuItemClick(item.callback)}
          >
            {item.display}
          </MenuItem>
        ))}
      </Menu>
    </FlexContainer>
  );
};

export default DocketFollowUpMobileView;
